import { Cancel, Done, Edit, Print } from "@mui/icons-material";
import {
  Autocomplete,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  Table,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { useState, useContext, SyntheticEvent } from "react";
import CustomTableBody from "../../../utilis/component/CustomTableBody";
import { RadiologyField, Template } from "../Type";
import { FormOnChangeEventType } from "../../../EMR/types";
import { ContextType, RadiologyContext } from "./index";
import { useMutation, useQuery } from "react-query";
import { findRadiology, getRadioTemplate, printReport } from "../Services";
import { clinicId } from "../../../constants";
import useSaveRadioResult from "../hooks/useSaveRadioresult";
import { useParams } from "react-router-dom";
import UploadedFileList from "./UploadFileList";
import usePrint from "../../../hooks/usePrint";
import useAuthentication from "@hooks/useAuthentication";

const RadiologyResult = () => {
  const [resultList, setResultList] = useState<RadiologyField[]>([]);
  const [TemplateList, setTemplateList] = useState<Template[] | []>([]);
  const [selected, setSelected] = useState<number>(-1);
  const {user} = useAuthentication()
  const { testData, setResultView, ResultView,setIsCompleted,isCompleted } = useContext(
    RadiologyContext
  ) as ContextType;
  const [disable, setDisable] = useState<boolean>(true);
  const theme = useTheme();
  const { encounterId } = useParams();
  const { printPdf } = usePrint();
  const [edit, setEdit] = useState<{
    [key: number]: string;
  }>({});
  const { mutate: findResult, isLoading: findResultLoading } = useMutation(
    findRadiology,
    {
      onSuccess: (data: any) => {
        if (data?.radiologyItems.length > 0) {
          setIsCompleted(true)
          setResultList(data?.radiologyItems);
        }else{
          setIsCompleted(false)
        }
      },
    }
  );

  const success = () => {
    !isCompleted && setResultView(false);
    setSelected(-1);
  };

  const { mutate: SaveRadiology, isLoading: saveLoading } =
    useSaveRadioResult(success);
  const { isLoading: getTemplateLoading } = useQuery(
    ["findTemplate", ResultView],
    () =>
      getRadioTemplate({
        templateName: testData?.cptName || "",
        clincid: clinicId,
        packageMappingId: testData?.cptId || 0,
        labRequestId: testData?.radiologyId || 0,
        cptId: testData?.cptId || 0,
      }),
    {
      onSuccess: (data: Template[]) => {
        setTemplateList(data);
          testData?.radiologyId && findResult(testData?.radiologyId);
      },
      refetchOnWindowFocus: false,
      enabled: ResultView,
    }
  );
  const setTemplateOnTable = (value: Template) => {
    if (value?.fields?.length > 0) {
      const updatedData = value?.fields?.map((item: any, index: number) => ({
        ...item,
        id: index,
        templateId: value?.templateId,
      }));
      setResultList(updatedData);
    } else {
      setResultList([]);
    }
  };

  const saveResult = (data?:RadiologyField[]) => {
    if(data?.length==0){
      data=[...resultList]
    }
    const bodyArray = data?.map((item) => {
      return {
        encouterId: Number(encounterId),
        value: item.fieldValue,
        unit: item.fieldUnit,
        range: item.fieldRange,
        name: item.fieldName,
        radioRequestId: testData?.radiologyId||0,
        createdById: Number(user?.userId),
        clinicId: clinicId,
        templateId: item?.templateId,
        obsRadioId: item?.obsRadioId || 0,
        radiologyResultId: item?.radiologyResultId || 0,
        _isEditMode: false,
        _isNew: false,
        _isUpdated: false,
        _isDeleted: false,
        _prevState: null,
        _userId: 0,
        _defaultFieldName: "",
      };
    });
    SaveRadiology(bodyArray);
  };

  const { mutate: getprint, isLoading: printLoading } = useMutation(
    printReport,
    { onSuccess: (data) => printPdf(data?.response, "new-tab") }
  );

  return (
    <div className="p-2">
      <>
        <div className="grid grid-cols-12 mb-2">
          <div className="col-span-5 flex justify-start items-center ml-3">
            <Typography sx={{ fontWeight: "semi-bold" }}>
              Radiology Result of{" "}
              <span className="font-extrabold">{testData?.cptName}</span>
            </Typography>
          </div>

          <div className="col-span-4  flex justify-center items-center">
            {!isCompleted && (
              <Autocomplete
                disablePortal
                options={TemplateList || []}
                sx={{ width: 300 }}
                isOptionEqualToValue={(option: Template, value: Template) =>
                  option.templateId === value.templateId
                }
                onChange={(_: SyntheticEvent, value: Template | null) => {
                  setTemplateOnTable(value as Template); // Set the selected Template or null
                  setDisable(true); // Enable the save button when a template is selected
                }}
                getOptionLabel={(option: Template) => option.templateName}
                renderInput={(params) => (
                  <TextField {...params} label="choose template" />
                )}
              />
            )}
          </div>

          <div className="col-span-2 flex justify-end items-center gap-x-2">
            <div className=" flex ">
              {isCompleted && <UploadedFileList />}
              {isCompleted && (
                <IconButton
                  onClick={() => getprint(testData?.radiologyId as number)}
                >
                  {printLoading ? (
                    <CircularProgress color="success" size={20} />
                  ) : (
                    <Print color="success" />
                  )}
                </IconButton>
              )}
            </div>
            <div className="flex gap-x-5 me-3 justify-center items-center">
              <Button
                endIcon={
                  !isCompleted &&
                  saveLoading && (
                    <CircularProgress
                      sx={{ color: theme.palette.background.paper }}
                      size={20}
                    />
                  )
                }
                disabled={isCompleted || disable}
                onClick={()=>saveResult([])}
                sx={(theme) => ({ background: theme.palette.primary.main })}
              >
                {isCompleted ? "Saved" : "Save"}
              </Button>
              <Button
                onClick={() => setResultView(false)}
                sx={(theme) => ({
                  background: theme.palette.error.dark,
                  "&:hover": {
                    background: theme.palette.error.main,
                  },
                })}
              >
                close
              </Button>
            </div>
          </div>
        </div>
        <Divider />
        <Table size="small">
          <TableHead
            sx={(theme) => ({ backgroundColor: theme.palette.primary.main })}
          >
            {headings?.map((item: string) => (
              <TableCell
                sx={(theme) => ({ color: theme.palette.background.paper })}
                key={item}
              >
                {item}
              </TableCell>
            ))}
          </TableHead>
          <CustomTableBody
            alert={"No Items"}
            dataLoading={getTemplateLoading || findResultLoading}
            isData={Boolean(resultList?.length > 0)}
            totalSpan={{ col: 6, row: 3 }}
          >
            {resultList?.map((item: RadiologyField, index: number) => {
              return (
                <>
                  <TableRow key={item?.id}>
                    <TableCell width="10%">{index + 1}</TableCell>
                    <TableCell width="30%">{item?.fieldName}</TableCell>
                    <TableCell width="30%">
                      {selected == index ? (
                        <TextField
                          defaultValue={edit[index] || item?.fieldValue || ""}
                          onChange={(event: FormOnChangeEventType) => {
                            const update = { ...edit };
                            if (event.target.value) {
                              update[index] = event.target.value;
                            } else {
                              delete update[index];
                            }
                            setEdit(update);
                          }}
                          label="Enter Result Value"
                          variant="standard"
                        />
                      ) : (
                        item?.fieldValue
                      )}
                      {}
                    </TableCell>
                    <TableCell width="10%">{item?.fieldUnit}</TableCell>
                    <TableCell width="10%">{item?.fieldRange || "-"}</TableCell>

                    <TableCell className="cursor-pointer">
                      {!Boolean(selected == index) && (
                        <>
                          <IconButton
                            onClick={() => {
                              setEdit((prev) => ({
                                ...prev,
                                [index]: String(item.fieldValue || 0),
                              }));
                              setSelected(index);
                            }}
                          >
                            <Edit color="primary" />
                          </IconButton>
                        </>
                      )}

                      {index == selected && (
                        <>
                          <IconButton
                            onClick={() => {
                              if (isCompleted) {
                                const upadated = [...resultList];
                                upadated[index].fieldValue = Number(
                                  edit[index] || "0"
                                );
                                upadated[index].radioRequestId = testData?.radiologyId || 0;
                                setResultList(upadated);
                                saveResult([upadated[index]]);
                              } else {
                                const upadated = [...resultList];
                                upadated[index].fieldValue = Number(
                                  edit[index] || "0"
                                );
                                setSelected(-1);
                                setResultList(upadated);
                                setDisable(false);
                              }
                            }}
                          >
                            {saveLoading ? (
                              <CircularProgress size={18} />
                            ) : (
                              <Done color="primary" />
                            )}
                          </IconButton>
                          <IconButton
                            onClick={() => {
                              setSelected(-1);
                            }}
                          >
                            <Cancel color="error" />
                          </IconButton>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                </>
              );
            })}
          </CustomTableBody>
        </Table>
      </>
    </div>
  );
};

export default RadiologyResult;

const headings = ["sl No", "field Name", "Value", "Unit", "Range", "Actions"];

// const radiologyFields = [
//     {
//       field_value: 0,
//       template_id: 1971,
//       field_unit: "",
//       field_range: "",
//       obsRadio_id: 6,
//       field_name: "Findings"
//     },
//     {
//       field_value: 0,
//       template_id: 2258,
//       field_unit: "",
//       field_range: "",
//       obsRadio_id: 6,
//       field_name: "Comparison"
//     },
//     {
//       field_value: 0,
//       template_id: 2259,
//       field_unit: "",
//       field_range: "",
//       obsRadio_id: 6,
//       field_name: "Technique"
//     },
//     {
//       field_value: 0,
//       template_id: 2263,
//       field_unit: "",
//       field_range: "",
//       obsRadio_id: 6,
//       field_name: "Conclusion"
//     }
//   ];
